/**
 * Client-only UI state for the activity stream (notifications).
 *
 * Server state — the stream entries themselves — lives in TanStack Query (see
 * `useStream`). This store holds only the ephemeral UI concerns that need to be
 * driven from outside React, notably from the export request handler:
 *
 * - `panelOpen` — the notifications slide-out drawer.
 * - `watching` — a job we just kicked off (export/import) is in flight, so the
 *   stream list should fast-poll until its `pending` entry flips to its outcome.
 * - the transient "queued" popover anchored to the header bell (Chrome-style
 *   download affordance): instant proof the click did something, pointing at
 *   where the result will land.
 */
import { create } from 'zustand';
import { registerStoreReset } from './registry';

/** How long the transient queued-export popover stays up before auto-hiding. */
const NOTICE_DISMISS_MS = 6000;

export interface StreamUiState {
  /** Notifications drawer open. */
  panelOpen: boolean;
  /**
   * A job we initiated is in flight. While true, `useStream` fast-polls the
   * list so the user sees the entry flip from "in progress" to its outcome
   * without waiting on the 30s count poll. Cleared by `useStream` once no
   * `pending` entry remains.
   */
  watching: boolean;
  /** Transient queued-export popover (anchored to the bell) is showing. */
  noticeOpen: boolean;
  /** Message shown in that popover. */
  noticeMessage: string;

  openPanel: () => void;
  closePanel: () => void;
  startWatching: () => void;
  stopWatching: () => void;
  /** Show the transient queued popover; auto-dismisses after a few seconds. */
  showNotice: (message: string) => void;
  hideNotice: () => void;
}

// The auto-dismiss timer is not render state — keep it out of the store.
let noticeTimer: ReturnType<typeof setTimeout> | undefined;
function clearNoticeTimer(): void {
  if (noticeTimer) {
    clearTimeout(noticeTimer);
    noticeTimer = undefined;
  }
}

export const useStreamUiStore = create<StreamUiState>((set, get) => ({
  panelOpen: false,
  watching: false,
  noticeOpen: false,
  noticeMessage: '',

  // Opening the panel is the user acknowledging the activity — retire the
  // transient popover so the two affordances don't fight for attention.
  openPanel: () => {
    clearNoticeTimer();
    set({ panelOpen: true, noticeOpen: false });
  },
  closePanel: () => set({ panelOpen: false }),

  startWatching: () => set({ watching: true }),
  stopWatching: () => set({ watching: false }),

  showNotice: (message) => {
    clearNoticeTimer();
    set({ noticeOpen: true, noticeMessage: message });
    noticeTimer = setTimeout(() => get().hideNotice(), NOTICE_DISMISS_MS);
  },
  hideNotice: () => {
    clearNoticeTimer();
    set({ noticeOpen: false });
  },
}));

registerStoreReset(() => {
  clearNoticeTimer();
  useStreamUiStore.setState({
    panelOpen: false,
    watching: false,
    noticeOpen: false,
    noticeMessage: '',
  });
}, { onUserSwitch: false }); // an open drawer stays open across a switch (#1336)
